import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler, scrollIntoView } from "@msuite/ui/utils";
import {
	type TableTypeSchema,
	type TableTypesFormSchema,
	tableTypesFormSchema,
} from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import type { TableTypesTable } from "@msuite/utils/types";
import { Reorder } from "motion/react";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { client } from "@/hono";
import { TableTypeCard } from "./table-type-card";

/** Props Interface */
interface TableTypesFormProps {
	tableTypesTable: TableTypesTable;
	withIconSelect: boolean;
}

export function TableTypesForm({ tableTypesTable, withIconSelect }: TableTypesFormProps) {
	/** State */
	const [recentlyAdded, setRecentlyAdded] = useState<string[]>([]);

	/** Hooks */
	const qc = useQueryClient();
	const { t } = useTranslation();

	const methods = useForm({
		defaultValues: async () => {
			const response = await client.organizations["table-types"][":tableTypesTable"].$get({
				query: { state: ["active", "inactive"] },
				param: { tableTypesTable },
			});
			const data = await responseHandler(response);
			const types = data.tableTypes;
			return { types };
		},
		resolver: resolver(tableTypesFormSchema),
	});
	const tableTypes = useWatch({
		control: methods.control,
		name: "types",
	});

	function handleOnReorder(values: any) {
		methods.setValue("types", values);
	}

	function handleOnAddOption() {
		const current = methods.getValues("types") ?? [];
		const id = generateId();
		setRecentlyAdded([...recentlyAdded, id]);
		const newOption: TableTypeSchema = {
			id,
			workspaceId: null,
			name: "Type",
			color: "blue",
			icon: "arrows-random",
			ranking: current.length + 1,
			state: "active",
		};
		methods.setValue("types", [...current, newOption]);
		scrollIntoView({
			id: `editor-table-type-${id}`,
			timeout: 500,
		});
	}

	async function handleOnSubmit(json: TableTypesFormSchema) {
		await client.organizations["table-types"][":tableTypesTable"].$patch({
			json,
			param: { tableTypesTable },
		});
		await qc.invalidateQueries({
			predicate: invalidate("table-types"),
		});
		toast.success(t("general.saved"));
	}

	if (!tableTypes) return null;

	/** Render */
	return (
		<FormProvider {...methods}>
			<div
				data-cy-container={tableTypesTable}
				className="v-stack gap-4"
			>
				<form
					onSubmit={methods.handleSubmit(handleOnSubmit)}
					data-cy="table-types-form"
				>
					<Reorder.Group
						values={tableTypes || []}
						className="v-stack gap-4"
						onReorder={handleOnReorder}
					>
						{tableTypes?.map((tableType, index) => (
							<TableTypeCard
								key={tableType.id}
								tableType={tableType}
								index={index}
								isRecentlyAdded={recentlyAdded.includes(tableType.id)}
								withIconSelect={withIconSelect}
								handleOnAddOption={handleOnAddOption}
							/>
						))}
					</Reorder.Group>
				</form>
				<Divider variant="content-card" />
				<div className="h-stack justify-end">
					<Button
						onClick={handleOnAddOption}
						icon={<Icon name="Plus" />}
						data-cy="add-table-type-button"
					>
						{t("general.add")}
					</Button>
					<Button
						onClick={methods.handleSubmit(handleOnSubmit)}
						variant="primary"
						isLoading={methods.formState.isSubmitting}
					>
						{t("general.save")}
					</Button>
				</div>
			</div>
		</FormProvider>
	);
}
